import type {
	DashboardConfigAndWRMResourceData,
	DashboardConfigType,
} from '@atlassian/jira-dashboard-common/src/types.tsx';
// eslint-disable-next-line jira/import-whitelist
import { mapToReactGadgets } from '@atlassian/jira-dashboard-common/src/utils/map-to-react-gadgets.tsx';
import type { RouterDataContext } from '@atlassian/react-resource-router';
import getDashboardConfig, { getDashboardsConfig } from '../common/fetch-dashboard-config.tsx';
import fetchWrmBundleDescriptor from '../common/fetch-wrm-bundle-descriptor.tsx';
import generatePreloadLinkTags from '../common/generate-preload-link-tags.tsx';
import generateScriptTags from '../common/generate-script-tags.tsx';
import { getKey } from '../common/get-key.tsx';
import { gadgetsToUniqueSortedAmdModules } from '../utils.tsx';

const generateScriptTagAndPreloadedModuleNames = async (
	configs: DashboardConfigType[],
): Promise<DashboardConfigAndWRMResourceData[]> => {
	const gadgets = configs.flatMap((config) => config.gadgets);
	const preloadedModuleNames = gadgetsToUniqueSortedAmdModules(gadgets);
	const wrmBundleDescriptor = await fetchWrmBundleDescriptor(preloadedModuleNames);
	return configs.map((config) => {
		const scriptTags =
			[
				wrmBundleDescriptor ? generateScriptTags(wrmBundleDescriptor) : null,
				generatePreloadLinkTags(config),
			]
				.filter(Boolean)
				.join('\n') || null;

		return { ...config, scriptTags, preloadedModuleNames };
	});
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default async (context: RouterDataContext): Promise<DashboardConfigAndWRMResourceData[]> => {
	// this is either for single dashboard/wallboard or wallboard slide show with multiple dashboards
	const { route, query } = context;
	const originalConfigs: DashboardConfigType[] =
		route.name === 'dashboard-wallboard'
			? await getDashboardsConfig(query.dashboardId)
			: [await getDashboardConfig(getKey(context))];

	const configs = originalConfigs.map((config) => ({
		...config,
		gadgets: mapToReactGadgets(config.gadgets),
	}));

	if (!__SERVER__) {
		return configs.map((config) => ({ ...config, preloadedModuleNames: [] }));
	}

	return generateScriptTagAndPreloadedModuleNames(configs);
};
